<template>
  <div
    class="complex-filter-section"
    :class="{ 'complex-filter-section--bordered': showBorder }"
  >
    <div v-if="showHeader" class="complex-filter-section__header">
      <BittsAvatar :org="org" size="x-small" />
      <h2>{{ title }}</h2>
    </div>

    <template v-for="(group, index) in activeGroups" :key="group.sourceId">
      <div v-if="index > 0" class="complex-filter-section__and">
        <div class="complex-filter-section__and-line" />
        <span class="complex-filter-section__and-text">And</span>
        <div class="complex-filter-section__and-line" />
      </div>

      <ComplexFilterGroup
        :title="group.title"
        :data-source="group.dataSource"
        :filters="group.filtersMap"
        :expression="group.expression"
        :field-options="group.fields"
        :allow-nesting="group.allowNesting"
        :exclude-selected-fields="group.excludeSelectedFields"
        :disabled="disabled"
        :filter-mode="filterMode"
        @update:state="(state) => updateGroup(group.sourceId, state)"
        @delete:table="removeGroup(group.sourceId)"
      />
    </template>

    <div
      v-if="!disabled && filterMode !== 'readOnly'"
      class="complex-filter-section__actions"
    >
      <BittsDropdownMenu
        :options="dropdownOptions"
        :use-search="true"
        search-placeholder="Search fields"
        variant="default"
        mode="single"
        :disabled="isAllActive"
        :external-active-parents="activeCrmParents"
        @selected:options="(opts) => addGroup(opts[0])"
      >
        <BittsButton
          type="neutral"
          variant="ghost"
          text="New object filter"
          size="small"
          :left-icon="['far', 'plus']"
          :disabled="isAllActive"
        />
        <template #prefix="{ option }">
          <DataSourceAvatar
            v-if="isDataSourceType(option.dataSourceType)"
            :data-source="option.dataSourceType"
            size="x-small"
          />
        </template>
      </BittsDropdownMenu>

      <BittsButton
        v-if="deletable"
        class="ml-auto"
        type="neutral"
        variant="ghost"
        text="Delete partner filter"
        size="small"
        @click="$emit('delete')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsAvatar, BittsButton, BittsDropdownMenu } from '@crossbeam/bitts';

import { computed, inject, ref } from 'vue';

import DataSourceAvatar from '@/components/DataSourceAvatar.vue';

import { getDefaultsForDataType } from '@/constants/filter-operators';
import { ProvidedUserAddedFilterLabels } from '@/injectionKeys/lists';
import type { FilterFieldOption } from '@/types/filters';

import ComplexFilterGroup, {
  type FilterGroupState,
} from './ComplexFilterGroup.vue';
import type { NestedGroupFilter } from './ComplexFilterNestedGroup.vue';
import type { DataSourceType, FilterMode, GroupedFieldOption } from './types';

const { fields, filterMode = 'editable' } = defineProps<{
  title: string;
  org: { domain?: string | null; logo_url?: string | null };
  fields: GroupedFieldOption[];
  deletable?: boolean;
  showHeader?: boolean;
  showBorder?: boolean;
  disabled?: boolean;
  filterMode?: FilterMode;
}>();

defineEmits<{ delete: [] }>();

const userAddedFilterLabels = inject(
  ProvidedUserAddedFilterLabels,
  ref(new Set<string>()),
);

export type FilterGroupOutput = {
  sourceId: string | number;
  title: string;
  subtitle?: string;
  filters: NestedGroupFilter[];
  expression: string[];
};

type ActiveGroup = {
  sourceId: string | number;
  title: string;
  dataSource?: DataSourceType;
  dataSourceLabel: string;
  fields: FilterFieldOption[];
  allowNesting: boolean;
  excludeSelectedFields: boolean;
  filtersMap: Map<string, NestedGroupFilter>;
  expression: string[];
};

const model = defineModel<FilterGroupOutput[]>({ default: () => [] });

const DATA_SOURCE_LABELS: Partial<Record<DataSourceType, string>> = {
  crossbeam: 'Crossbeam',
  salesforce: 'Salesforce',
  hubspot_v3: 'HubSpot',
  microsoft_dynamics: 'Dynamics',
  pipedrive: 'Pipedrive',
  snowflake: 'Snowflake',
  databricks: 'Databricks',
  file_upload: 'CSV',
  google_sheets: 'Google Sheets',
};

function isDataSourceType(value: unknown): value is DataSourceType {
  return typeof value === 'string' && value in DATA_SOURCE_LABELS;
}

// Maps field config by ID for use when hydrating active groups
const configMap = computed(() => {
  const map = new Map<string | number, GroupedFieldOption>();
  fields.forEach((g) => map.set(g.id, g));
  return map;
});

// Hydrates saved filter groups with config data (fields, nesting rules, data source info)
const activeGroups = computed<ActiveGroup[]>(() => {
  return model.value.map((savedGroup) => {
    const config = configMap.value.get(savedGroup.sourceId);

    // Map filters for child component consumption
    const filtersMap = new Map(savedGroup.filters.map((f) => [f.label, f]));

    return {
      sourceId: savedGroup.sourceId,
      title: config?.label ?? savedGroup.title,
      dataSource: config?.dataSource,
      dataSourceLabel: config?.dataSourceLabel ?? '',
      fields: config?.options ?? [],
      allowNesting: config?.allowNesting ?? true,
      excludeSelectedFields: config?.excludeSelectedFields ?? false,
      filtersMap,
      expression: savedGroup.expression,
    };
  });
});

const isAllActive = computed(() => model.value.length === fields.length);

function getParentValue(config: GroupedFieldOption): string {
  const dataSource = config.dataSource ?? 'other';
  if (dataSource === 'crossbeam') return 'crossbeam';
  if (config.feedId) return `${dataSource}_${config.feedId}`;
  if (config.datasetName) return `${dataSource}_${config.datasetName}`;
  return dataSource;
}

const activeCrmParents = computed(() => {
  const parents = new Set<string>();
  for (const group of model.value) {
    const config = configMap.value.get(group.sourceId);
    if (config) {
      parents.add(getParentValue(config));
    }
  }
  return parents;
});

// Data sources that should be grouped by datasetName (file uploads, sheets)
const DATASET_GROUPED_SOURCES = new Set(['file_upload', 'google_sheets']);

/**
 * Builds dropdown options, excluding already active groups, sorted alphabetically.
 * - Groups CRM sources by type when multiple CRMs are present
 * - Groups file uploads/sheets by datasetName (related sources share same dataset)
 */
const dropdownOptions = computed(() => {
  const selectedIds = new Set(model.value.map((g) => g.sourceId));
  const available = fields.filter((g) => !selectedIds.has(g.id));

  // Separate CRM sources from dataset-grouped sources (file uploads, sheets)
  const crmSources = available.filter(
    (g) => !DATASET_GROUPED_SOURCES.has(g.dataSource ?? ''),
  );
  const datasetSources = available.filter((g) =>
    DATASET_GROUPED_SOURCES.has(g.dataSource ?? ''),
  );

  // Check if we have multiple distinct CRM sources
  const uniqueDataSources = new Set(
    crmSources.map((g) => g.dataSourceLabel || g.dataSource).filter(Boolean),
  );
  const hasMultipleCrms = uniqueDataSources.size > 1;

  // Helper to build field options for a table/source group
  function buildGroupOptions(g: GroupedFieldOption) {
    return {
      label: g.label,
      useBorder: g.useBorder ?? true,
      options: g.options
        .map(({ options: _fieldOptions, ...f }) => ({
          ...f,
          groupTitle: g.label,
          sourceId: g.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    };
  }

  // Build items for dataset-grouped sources (file uploads, sheets)
  // Sources with same datasetName are grouped together (e.g., CSV + its User table)
  function buildDatasetItems() {
    const byDataset = new Map<string, GroupedFieldOption[]>();

    for (const g of datasetSources) {
      // Use datasetName to group related sources, fall back to label if not set
      const key = g.datasetName || g.label;
      const existing = byDataset.get(key) ?? [];
      byDataset.set(key, [...existing, g]);
    }

    return Array.from(byDataset.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([datasetKey, sources]) => {
        const firstSource = sources[0];
        const displayLabel = firstSource?.label || datasetKey;
        return {
          label: displayLabel,
          value: firstSource ? getParentValue(firstSource) : datasetKey,
          dataSourceType: firstSource?.dataSource,
          children: sources
            .map(buildGroupOptions)
            .sort((a, b) => a.label.localeCompare(b.label)),
        };
      });
  }

  // If multiple CRMs, nest CRM sources under CRM labels using children
  if (hasMultipleCrms) {
    const grouped = new Map<string, GroupedFieldOption[]>();

    for (const g of crmSources) {
      const key = g.dataSourceLabel || g.dataSource || 'Other';
      const existing = grouped.get(key) ?? [];
      grouped.set(key, [...existing, g]);
    }

    const crmItems = Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, groups]) => {
        const firstGroup = groups[0];
        const displayLabel =
          firstGroup?.dataSourceLabel ||
          (firstGroup?.dataSource
            ? DATA_SOURCE_LABELS[firstGroup.dataSource]
            : undefined) ||
          key;

        return {
          label: displayLabel,
          value: firstGroup ? getParentValue(firstGroup) : key,
          dataSourceType: firstGroup?.dataSource,
          children: groups
            .map(buildGroupOptions)
            .sort((a, b) => a.label.localeCompare(b.label)),
        };
      });

    // Add dataset-grouped sources (file uploads, sheets)
    const datasetItems = buildDatasetItems();

    return [...crmItems, ...datasetItems];
  }

  // Single CRM - flat list for CRM sources, but group dataset sources
  if (datasetSources.length > 0) {
    const crmItems = crmSources
      .map(buildGroupOptions)
      .sort((a, b) => a.label.localeCompare(b.label));
    const datasetItems = buildDatasetItems();
    return [...crmItems, ...datasetItems];
  }

  return available
    .map(buildGroupOptions)
    .sort((a, b) => a.label.localeCompare(b.label));
});

function addGroup(opt?: {
  value?: string | number;
  sourceId?: string | number;
}) {
  if (!opt?.value || !opt?.sourceId) return;

  const config = configMap.value.get(opt.sourceId);
  if (!config) return;

  const field = config.options.find((f) => f.value === opt.value);
  const defaults = getDefaultsForDataType(field?.dataType);
  const id = crypto.randomUUID();

  const newGroup: FilterGroupOutput = {
    sourceId: opt.sourceId,
    title: config.label,
    subtitle: config.dataSourceLabel,
    filters: [
      {
        label: id,
        value: opt.value,
        operator: field?.allowedOperators?.at(0) ?? defaults.operator,
        operand: defaults.operand,
      },
    ],
    expression: [id],
  };
  if (filterMode === 'hybrid') userAddedFilterLabels.value.add(id);

  model.value = [...model.value, newGroup];
}

function updateGroup(sourceId: string | number, state: FilterGroupState) {
  const index = model.value.findIndex((g) => g.sourceId === sourceId);
  const existing = model.value[index];
  if (index === -1 || !existing) return;

  const updatedGroup: FilterGroupOutput = {
    ...existing,
    filters: Array.from(state.filters.values()),
    expression: state.expression,
  };

  const newModel = [...model.value];
  newModel[index] = updatedGroup;
  model.value = newModel;
}

function removeGroup(sourceId: string | number) {
  model.value = model.value.filter((g) => g.sourceId !== sourceId);
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

.complex-filter-section {
  @apply flex flex-col gap-12;
}

.complex-filter-section--bordered {
  @apply border-t border-neutral-border pt-16;
}

.complex-filter-section__header {
  @apply flex items-center gap-8 text-neutral-text-strong font-bold;
}

.complex-filter-section__and {
  @apply flex items-center gap-8 -my-8;
}

.complex-filter-section__and-line {
  @apply flex-1 h-px bg-neutral-border;
}

.complex-filter-section__and-text {
  @apply text-sm text-neutral-text-weak;
}

.complex-filter-section__actions {
  @apply flex items-center gap-8;
}
</style>
